import { CenterSpinner } from "~/atoms/spinner";
import { type ReaderSummary } from "~/api/types";
import { useReaderReport } from "~/api/v2/useReaderReport";
import { deleteReader } from "~/api/v2/requests";
import { Badge } from "~/atoms/badge";
import { Button } from "~/atoms/button";
import { Card } from "~/atoms/card";
import { toast } from "react-toastify";
import {
  Table,
  TableHeader,
  TableBody,
  TableHead,
  TableRow,
  TableCell,
} from "~/atoms/table";
import { Trash } from "lucide-react";
import { ConfirmDeletePopover } from "~/molecules/ConfirmDeletePopover";
import { useState } from "react";
import { useTranslation } from "react-i18next";
import NoResult from "~/molecules/NoResult";
import ErrorState from "~/molecules/ErrorState";

interface Props {
  documentVersionId: number;
  isRemoveAllowed: boolean;
}

export const ReadersOverview = ({
  documentVersionId,
  isRemoveAllowed,
}: Props) => {
  const { t } = useTranslation();

  const readerTableColumnCount = 2 + (isRemoveAllowed ? 1 : 0);

  const [isRemoving, setIsRemoving] = useState<boolean>(false);
  const {
    data: readerReport,
    isLoading,
    error,
    mutate,
  } = useReaderReport(documentVersionId);

  if (isLoading) {
    return <CenterSpinner />;
  }

  if (error) {
    return <ErrorState />;
  }

  if (!readerReport) {
    return <NoResult />;
  }

  const handleRemove = async (userId: number) => {
    try {
      setIsRemoving(true);
      await deleteReader(documentVersionId, userId);
      void mutate();
    } catch {
      toast.error(t(($) => $.toasts.error.generic));
    } finally {
      setIsRemoving(false);
    }
  };

  if (readerReport === undefined) {
    return <NoResult />;
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="grid grid-cols-3 gap-4">
        <Card className="p-4">
          <div className="text-muted-foreground text-sm">
            {t(($) => $.commons.workflow.total)}
          </div>
          <div className="text-2xl font-bold">{readerReport.summary.total}</div>
        </Card>
        <Card className="p-4">
          <div className="text-muted-foreground text-sm">
            {t(($) => $.commons.workflow.opened)}
          </div>
          <div className="text-2xl font-bold">
            {readerReport.summary.opened}
          </div>
        </Card>
        <Card className="p-4">
          <div className="text-muted-foreground text-sm">
            {t(($) => $.commons.workflow.accepted)}
          </div>
          <div className="text-2xl font-bold">{readerReport.summary.read}</div>
        </Card>
      </div>

      <Card className="p-0">
        <Table className="table-fixed">
          <colgroup>
            <col className="w-auto" />
            <col className="w-28" />
            {isRemoveAllowed && <col className="w-16" />}
          </colgroup>
          <TableHeader className="bg-muted">
            <TableRow>
              <TableHead>{t(($) => $.commons.workflow.reader)}</TableHead>
              <TableHead className="whitespace-nowrap">
                {t(($) => $.commons.workflow.status)}
              </TableHead>
              {isRemoveAllowed && <TableHead />}
            </TableRow>
          </TableHeader>
          <TableBody>
            {readerReport.readers.length > 0 ? (
              readerReport.readers.map((reader: ReaderSummary) => (
                <TableRow key={reader.userId}>
                  <TableCell className="max-w-0">
                    <div className="flex min-w-0 items-center">
                      <div className="min-w-0">
                        <div className="truncate text-sm font-medium">
                          {reader.firstName} {reader.lastName}
                        </div>
                        <div
                          className="text-muted-foreground truncate text-sm"
                          title={reader.email}
                        >
                          {reader.email}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell className="whitespace-nowrap">
                    <Badge
                      variant={
                        reader.hasRead
                          ? "success"
                          : reader.hasOpened
                            ? "warn"
                            : "secondary"
                      }
                    >
                      {reader.hasRead
                        ? t(($) => $.commons.workflow.accepted)
                        : reader.hasOpened
                          ? t(($) => $.commons.workflow.opened)
                          : t(($) => $.commons.workflow.notOpened)}
                    </Badge>
                  </TableCell>
                  {isRemoveAllowed && (
                    <TableCell className="text-right">
                      <ConfirmDeletePopover
                        trigger={
                          <Button
                            variant="ghost"
                            size="icon"
                            disabled={isRemoving}
                          >
                            <Trash />
                          </Button>
                        }
                        onConfirm={() => handleRemove(reader.userId)}
                      />
                    </TableCell>
                  )}
                </TableRow>
              ))
            ) : (
              <TableRow>
                <TableCell
                  colSpan={readerTableColumnCount}
                  className="text-muted-foreground text-center italic"
                >
                  {t(($) => $.commons.workflow.noReaders)}
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </Card>
    </div>
  );
};
